import { motion } from 'framer-motion';
import { Scan, Layers, Zap } from 'lucide-react';
import { Reveal, fadeUp, stagger } from './helpers';

const pillars = [
    {
        icon: Scan,
        title: 'Extracted, not asked.',
        body: "AI surfaces the decisions an expert wouldn't think to mention — the muscle-memory calls, the unwritten rules, the reasoning they never articulate because, to them, it's obvious.",
    },
    {
        icon: Layers,
        title: 'Structured, not written.',
        body: 'Every decision becomes a short, queryable note: the situation, the call, the reasoning, the rule of thumb. Not a 40-page handover document nobody opens.',
    },
    {
        icon: Zap,
        title: 'Transferred, not stored.',
        body: "The replacement asks questions in plain English and gets answers in the expert's own voice — cited back to source. The playbook isn't filed away; it's an always-on advisor.",
    },
];

export const InOneLine = () => (
    <section id="in-one-line" className="py-28 scroll-mt-16 border-t border-border/40 bg-muted/20">
        <div className="max-w-5xl mx-auto px-6">
            <Reveal className="text-center max-w-4xl mx-auto mb-16">
                <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-6">What that looks like in practice</p>
                <p className="text-2xl sm:text-3xl lg:text-[40px] font-semibold tracking-[-0.02em] leading-[1.2] text-foreground">
                    We don't ask what they did. We reconstruct <span className="text-primary">how they thought</span> — and turn it into a working system the next person can actually use.
                    <span className="block mt-3 text-muted-foreground/70 text-xl sm:text-2xl lg:text-[26px] font-normal leading-relaxed">
                        Surfaced by an AI interview coach. Validated by the expert. Delivered as a playbook that answers questions, not a document that sits in a drive.
                    </span>
                </p>
            </Reveal>

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {pillars.map((p) => (
                    <motion.div key={p.title} variants={fadeUp} className="rounded-2xl border border-border/60 bg-card p-7">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-5">
                            <p.icon className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold tracking-tight mb-2 leading-snug">{p.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                    </motion.div>
                ))}
            </motion.div>

            <Reveal>
                <p className="mt-10 text-center text-sm text-muted-foreground">Two weeks, end to end. One fixed price per departure. No annual contract.</p>
            </Reveal>
        </div>
    </section>
);
